import React, {useEffect, useState} from 'react';
import {Box, Drawer, IconButton, Stack} from '@mui/material';
import {Body, Button, Icon} from '@lightricks/react-design-system';
import {queryClient} from '@/utils/reactQueryClient';
import useConversationMessagesQuery from '@/hooks/queries/messages/use-conversation-messages-query';
import useUnifiedMessagesQuery from '@/hooks/queries/messages/use-unified-messages-query';
import useSendMessage from '@/hooks/queries/mutations/use-send-message';
import {
  conversationMessagesQueryKey,
  markAllConversationMessagesRead,
  markConversationRead,
  markVersionMessagesRead,
  respondToNegotiation,
  unifiedMessagesQueryKey,
  NegotiationResponse,
} from '@/api/messages';
import {FAQ_URL} from '@/api/messages/constants';
import {
  AssetMessageThread,
  Message,
  SendMessagePayload,
  UnifiedFeedItem,
} from '@/api/messages/types';
import {paidGigDetailQueryKey} from '@/hooks/queries/gigs/use-paid-gig-detail-query';
import {trackDeliverables, captureDeliverablesError} from '../telemetry';
import MessageThread from './MessageThread';
import styles from './MessagesPanel.module.scss';

interface Props {
  open: boolean;
  gigId: string;
  campaignId: string;
  conversationId: string | null;
  supportConversationId: string | null;
  merchantName: string;
  negotiationResolved: boolean;
  onOpenAssetThread: (thread: AssetMessageThread) => boolean;
  onClose: () => void;
}

const SWAPS_NEGOTIATION_LINK_RE = /\/paid_gigs\/[^"')\s]*\/negotiations/;
const NEGOTIATION_STATE_PENDING = 'pending';
const OPTIMISTIC_NEGOTIATION_PREFIX = 'optimistic-negotiation-';

function invalidateUnreadQueries(gigId: string) {
  return Promise.all([
    queryClient.invalidateQueries({queryKey: paidGigDetailQueryKey(gigId)}),
    queryClient.invalidateQueries({queryKey: ['user']}),
  ]);
}

function isResolvedSwapsOffer(message: {
  body: string;
  bodyHtml: string | null;
}) {
  return (
    SWAPS_NEGOTIATION_LINK_RE.test(message.bodyHtml ?? '') ||
    SWAPS_NEGOTIATION_LINK_RE.test(message.body ?? '')
  );
}

export default function MessagesPanel({
  open,
  gigId,
  campaignId,
  conversationId,
  supportConversationId,
  merchantName,
  negotiationResolved,
  onOpenAssetThread,
  onClose,
}: Props) {
  const [showSupport, setShowSupport] = useState(false);
  const [respondedOffers, setRespondedOffers] = useState<
    Record<string, string>
  >({});
  const activeConversationId = showSupport
    ? supportConversationId
    : conversationId;

  useEffect(() => {
    if (open) setShowSupport(false);
  }, [open]);

  const supportMessagesQuery = useConversationMessagesQuery(
    activeConversationId,
    {enabled: open && showSupport}
  );
  const unifiedMessagesQuery = useUnifiedMessagesQuery(
    conversationId,
    campaignId,
    {enabled: open && !showSupport}
  );
  const sendMessage = useSendMessage(
    {kind: 'conversation', id: activeConversationId ?? ''},
    {
      onError: (e) =>
        captureDeliverablesError(e, 'Failed to send message', {
          gigId,
          conversationId: activeConversationId,
        }),
    }
  );

  useEffect(() => {
    if (!open || !activeConversationId) return;
    if (
      showSupport &&
      (supportMessagesQuery.isLoading || supportMessagesQuery.isError)
    )
      return;
    if (!showSupport && !unifiedMessagesQuery.isSuccess) return;
    trackDeliverables('Opened messages panel', {
      gigId,
      conversationId: activeConversationId,
      support: showSupport,
    });
    const markRead = showSupport
      ? markConversationRead(activeConversationId)
      : markAllConversationMessagesRead(activeConversationId, campaignId);
    markRead
      .then(() => invalidateUnreadQueries(gigId))
      .catch((e) =>
        captureDeliverablesError(e, 'Failed to mark conversation read', {
          gigId,
          conversationId: activeConversationId,
        })
      );
  }, [
    open,
    activeConversationId,
    campaignId,
    gigId,
    showSupport,
    supportMessagesQuery.isError,
    supportMessagesQuery.isLoading,
    unifiedMessagesQuery.isSuccess,
  ]);

  const handleOpenAssetThread = (thread: AssetMessageThread) => {
    if (!onOpenAssetThread(thread)) return;

    markVersionMessagesRead(thread.versionId)
      .then(() =>
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: unifiedMessagesQueryKey(conversationId ?? '', campaignId),
          }),
          invalidateUnreadQueries(gigId),
        ])
      )
      .catch((e) =>
        captureDeliverablesError(e, 'Failed to mark asset messages read', {
          gigId,
          versionId: thread.versionId,
        })
      );
  };

  const handleSend = async (payload: SendMessagePayload) => {
    const result = await sendMessage.mutateAsync(payload);
    if (!showSupport && conversationId) {
      await queryClient.invalidateQueries({
        queryKey: unifiedMessagesQueryKey(conversationId, campaignId),
      });
    }
    return result;
  };

  const handleRespondToNegotiation = async (
    action: NegotiationResponse,
    brandNegotiationId: string
  ) => {
    const newState = action === 'accept' ? 'accepted' : 'rejected';
    setRespondedOffers((prev) => ({...prev, [brandNegotiationId]: newState}));
    try {
      await respondToNegotiation(action, brandNegotiationId);
      await queryClient.invalidateQueries({
        queryKey: conversationMessagesQueryKey(activeConversationId ?? ''),
      });
      if (conversationId) {
        await queryClient.invalidateQueries({
          queryKey: unifiedMessagesQueryKey(conversationId, campaignId),
        });
      }
      await queryClient.invalidateQueries({
        queryKey: paidGigDetailQueryKey(gigId),
      });
    } catch (e) {
      setRespondedOffers((prev) => {
        const next = {...prev};
        delete next[brandNegotiationId];
        return next;
      });
      throw e;
    }
  };

  const chronologicalFeed = [...unifiedMessagesQuery.items].reverse();
  const sourceMessages = showSupport
    ? supportMessagesQuery.messages
    : chronologicalFeed.flatMap((item) =>
        item.kind === 'message' ? [item.data] : []
      );
  const visibleMessages = sourceMessages.filter(
    (message) => !(negotiationResolved && isResolvedSwapsOffer(message))
  );

  const resolvedSnapshotKeys = new Set(
    visibleMessages
      .filter(
        (m) =>
          m.negotiationSnapshot &&
          m.negotiationSnapshot.state !== NEGOTIATION_STATE_PENDING
      )
      .map(
        (m) =>
          `${m.negotiationSnapshot?.brandNegotiationId}:${m.negotiationSnapshot?.state}`
      )
  );

  const lastOwnMessage = [...visibleMessages]
    .reverse()
    .find((m) => m.fromCurrentUser);

  const displayedMessages: Message[] = visibleMessages.map((message) => {
    const bnId = message.negotiationSnapshot?.brandNegotiationId;
    const respondedState = bnId ? respondedOffers[bnId] : undefined;
    if (
      respondedState &&
      message.negotiationSnapshot?.state === NEGOTIATION_STATE_PENDING
    ) {
      return {
        ...message,
        negotiationSnapshot: {
          ...message.negotiationSnapshot,
          isLatestOffer: false,
        },
      };
    }
    return message;
  });

  // Mirror the backend: responding to an offer creates a new snapshot message.
  // Append it optimistically until the server refetch includes the real one.
  Object.entries(respondedOffers).forEach(([bnId, state]) => {
    if (resolvedSnapshotKeys.has(`${bnId}:${state}`)) return;
    const source = visibleMessages.find(
      (m) => m.negotiationSnapshot?.brandNegotiationId === bnId
    );
    if (!source?.negotiationSnapshot) return;
    displayedMessages.push({
      ...source,
      id: `${OPTIMISTIC_NEGOTIATION_PREFIX}${bnId}`,
      body: '',
      bodyHtml: null,
      createdAt: new Date().toISOString(),
      attachments: [],
      sender: lastOwnMessage?.sender ?? source.sender,
      displayName: lastOwnMessage?.displayName ?? source.displayName,
      fromCurrentUser: true,
      negotiationSnapshot: {
        ...source.negotiationSnapshot,
        state,
        isLatestOffer: true,
      },
    });
  });

  const displayedMessagesById = new Map(
    displayedMessages.map((message) => [message.id, message])
  );
  const displayedFeedItems = chronologicalFeed.reduce<UnifiedFeedItem[]>(
    (items, item) => {
      if (item.kind === 'versionThread') {
        items.push(item);
        return items;
      }
      const message = displayedMessagesById.get(item.data.id);
      if (message) items.push({kind: 'message', data: message});
      return items;
    },
    []
  );
  const feedMessageIds = new Set(
    displayedFeedItems.flatMap((item) =>
      item.kind === 'message' ? [item.data.id] : []
    )
  );
  displayedMessages.forEach((message) => {
    if (!feedMessageIds.has(message.id)) {
      displayedFeedItems.push({kind: 'message', data: message});
    }
  });

  const isLoading = showSupport
    ? supportMessagesQuery.isLoading
    : unifiedMessagesQuery.isLoading;
  const isError = showSupport
    ? supportMessagesQuery.isError
    : unifiedMessagesQuery.isError;

  const emptyState = showSupport ? (
    <>
      <Body size="lg">
        <strong>How can we help?</strong>
      </Body>
      <Body size="md">We usually respond within one business day.</Body>
    </>
  ) : (
    <Body size="lg">
      Talk to <strong>{merchantName}</strong> about Gig related questions.
    </Body>
  );

  const faqFooter = (
    <Button
      appearance="neutral"
      mode="plain"
      size="small"
      icon={
        <Icon
          size="small"
          appearance="neutral"
          name="Navigation-Question-Line"
        />
      }
      iconPosition="start"
      onClick={() => window.open(FAQ_URL, '_blank', 'noopener,noreferrer')}
    >
      Check out our FAQ
    </Button>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{paper: styles.panel}}
    >
      <Stack
        direction="column"
        sx={{height: '100%', minHeight: 0, position: 'relative'}}
      >
        <IconButton
          className={styles.close}
          aria-label="Close messages"
          data-testid="messages-panel-close"
          onClick={onClose}
        >
          <Icon size="small" appearance="neutral" name="Actions-Close-Normal" />
        </IconButton>

        {showSupport && (
          <Stack
            component="button"
            type="button"
            direction="row"
            alignItems="center"
            spacing={0.5}
            className={styles.supportBack}
            data-testid="support-back"
            onClick={() => setShowSupport(false)}
          >
            <Box
              component="span"
              className={styles.supportBackIcon}
              aria-hidden="true"
            >
              ←
            </Box>
            <Body size="sm">
              back to <strong>{merchantName}</strong>
            </Body>
          </Stack>
        )}

        {!showSupport && supportConversationId && (
          <Stack
            component="button"
            type="button"
            direction="row"
            alignItems="center"
            spacing={1}
            className={styles.supportButton}
            data-testid="contact-support"
            onClick={() => setShowSupport(true)}
          >
            <Icon
              className={styles.supportButtonIcon}
              size="small"
              appearance="neutral"
              name="Navigation-Messages"
            />
            <Body size="sm" className={styles.supportButtonText}>
              <Box component="span" className={styles.supportPrefix}>
                Need help with Payments, Uploads or Platform Issues?{' '}
              </Box>
              <strong>Contact Support</strong>
            </Body>
          </Stack>
        )}

        <MessageThread
          messages={displayedMessages}
          feedItems={showSupport ? undefined : displayedFeedItems}
          firstUnreadMessageId={
            showSupport ? null : unifiedMessagesQuery.firstUnreadMessageId
          }
          isLoading={isLoading}
          isError={isError}
          onSend={handleSend}
          allowAttachments={!showSupport}
          emptyState={emptyState}
          footer={showSupport ? faqFooter : undefined}
          hasOlder={!showSupport && unifiedMessagesQuery.hasNextPage}
          isLoadingOlder={unifiedMessagesQuery.isFetchingNextPage}
          onLoadOlder={
            showSupport ? undefined : unifiedMessagesQuery.fetchNextPage
          }
          onOpenAssetThread={handleOpenAssetThread}
          onRespondToNegotiation={handleRespondToNegotiation}
        />
      </Stack>
    </Drawer>
  );
}
